import React, { createContext, useEffect, useState } from "react";

const AuthContext = createContext();

const pkgBaseUrl = process.env.REACT_APP_API_BASE_URL;
const apiver = "api/v3";
const apiBaseUrl = `${pkgBaseUrl}/${apiver}`;

const bypassFreemium = process.env.REACT_APP_BYPASS_FREEMIUM === "true";
const applyBypass = (usr) =>
  usr && bypassFreemium ? { ...usr, plan: "pro_monthly" } : usr;

export const AuthProvider = ({ children }) => {
  const [tokenPair, setTokenPair] = useState(() => {
    const stored = localStorage.getItem("tokenPair");
    return stored ? JSON.parse(stored) : null;
  });

  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return applyBypass(stored ? JSON.parse(stored) : null);
  });

  const saveTokenPair = (pair) => {
    setTokenPair(pair);
    if (pair) {
      localStorage.setItem("tokenPair", JSON.stringify(pair));
    } else {
      localStorage.removeItem("tokenPair");
    }
  };

  const saveUser = (usr) => {
    const effective = applyBypass(usr);
    setUser(effective);
    if (effective) {
      localStorage.setItem("user", JSON.stringify(effective));
    } else {
      localStorage.removeItem("user");
    }
  };

  const fetchMe = async (access) => {
    const bearer = access || tokenPair?.access;
    if (!bearer) {
      return null;
    }
    const res = await fetch(`${apiBaseUrl}/auth/me`, {
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
    });
    if (res.status === 404) {
      saveUser(null);
      return null;
    }
    if (!res.ok) {
      throw new Error("Failed to fetch user");
    }
    const data = await res.json();
    const usr = data.user || data;
    saveUser(usr);
    return usr;
  };

  const login = async (email, password) => {
    const res = await fetch(`${apiBaseUrl}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      throw new Error("Login failed");
    }
    const data = await res.json();
    saveTokenPair({ access: data.access, refresh: data.refresh });
    if (data.user) {
      saveUser(data.user);
    } else if (data.access) {
      await fetchMe(data.access);
    }
    return data;
  };

  const signup = async ({ email, password, plan, displayName, billing }) => {
    const payload = { email, password, plan };
    if (displayName) {
      payload.displayName = displayName;
    }
    if (billing && Object.keys(billing).length > 0) {
      payload.billing = billing;
    }
    const res = await fetch(`${apiBaseUrl}/auth/signup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      throw new Error("Signup failed");
    }
    const data = await res.json();
    saveTokenPair({ access: data.access, refresh: data.refresh });
    if (data.user) {
      saveUser(data.user);
    }
    return data;
  };

  const guestLogin = async () => {
    const res = await fetch(`${apiBaseUrl}/auth/guest`, { method: "POST" });
    if (!res.ok) {
      throw new Error("Guest login failed");
    }
    const data = await res.json();
    saveTokenPair({ access: data.access, refresh: data.refresh });
    if (data.user) {
      saveUser(data.user);
    }
    return data;
  };

  const logout = async () => {
    if (tokenPair?.refresh) {
      await fetch(`${apiBaseUrl}/auth/logout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${tokenPair.refresh}`,
        },
      });
    }
    saveTokenPair(null);
    saveUser(null);
  };

  useEffect(() => {
    if (!tokenPair) {
      guestLogin().catch(() => {});
    } else if (!user) {
      fetchMe().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ tokenPair, user, login, signup, logout, guestLogin }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
